import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Select,
  Table,
  Tag,
  Alert,
  Switch,
  Divider,
  message,
  Modal,
  Space,
  Badge,
  Tooltip,
  Spin,
  Empty,
} from "antd";
import {
  PlayCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FontSizeOutlined,
  LinkOutlined,
  BorderOutlined,
  AppstoreOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import PlaceholderVisualizer from "./PlaceholderVisualizer";
import {
  getTemplates,
  getTemplate,
  getDataFiles,
  getDataFile,
  getDataFilePreview,
  getFonts,
  uploadDataFile,
  testGenerate,
  generateBatch,
  updateTemplateMappings,
  getGenerationFiles,
  downloadGenerationZip,
} from "../../core/api/pdfParser";
import useGlobalFilters from "../../hooks/useGlobalFilters";

const { Option } = Select;

function GeneratePdf() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { brandIds, hasSelectedBrands } = useGlobalFilters();
  const currentBrandId = brandIds.length > 0 ? brandIds[0] : null;

  const templateIdFromUrl = searchParams.get("templateId");
  const dataPathFromUrl = searchParams.get("dataPath");

  const [template, setTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateIdFromUrl ? parseInt(templateIdFromUrl) : null
  );
  const [dataPath, setDataPath] = useState(dataPathFromUrl || "");

  // Update URL when template or data changes for persistence across tab switches
  const updateUrlParams = (newTemplateId, newDataPath) => {
    const params = new URLSearchParams(searchParams);
    if (newTemplateId) {
      params.set("templateId", newTemplateId);
    } else {
      params.delete("templateId");
    }
    if (newDataPath) {
      params.set("dataPath", newDataPath);
    } else {
      params.delete("dataPath");
    }
    setSearchParams(params, { replace: true });
  };

  // Wrapper for setSelectedTemplateId that also updates URL
  const handleTemplateSelect = (id) => {
    setSelectedTemplateId(id);
    updateUrlParams(id, dataPath);
  };

  // Wrapper for setDataPath that also updates URL
  const handleDataPathChange = (path) => {
    setDataPath(path);
    updateUrlParams(selectedTemplateId, path);
  };
  const [dataInfo, setDataInfo] = useState(null);
  const [dataFiles, setDataFiles] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [fontMap, setFontMap] = useState({});
  const [placeholderSettings, setPlaceholderSettings] = useState({}); // {placeholderName: {alignment, overflow, minFontSize, type}}
  const [availableFonts, setAvailableFonts] = useState([]);
  const [idColumn, setIdColumn] = useState("person_id");
  const [combine, setCombine] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [batchPreviewVisible, setBatchPreviewVisible] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);
  const [loadingBatchFiles, setLoadingBatchFiles] = useState(false);
  const [previewSectionError, setPreviewSectionError] = useState(false);
  const lastFetchedDataPathRef = useRef(null);

  // Local session storage for data persistence across navigation
  const SESSION_KEY = 'pdf_parser_session';
  
  // Load session state on mount - prioritize URL params, then sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        // Restore state if template matches URL param or if no URL param but we have saved data
        const shouldRestore = !templateIdFromUrl || (data.templateId && data.templateId === parseInt(templateIdFromUrl));
        if (shouldRestore) {
          // Restore mappings and settings
          if (data.columnMap && Object.keys(data.columnMap).length > 0) setColumnMap(data.columnMap);
          if (data.fontMap && Object.keys(data.fontMap).length > 0) setFontMap(data.fontMap);
          if (data.placeholderSettings && Object.keys(data.placeholderSettings).length > 0) setPlaceholderSettings(data.placeholderSettings);
          if (data.idColumn) setIdColumn(data.idColumn);
          if (data.combine !== undefined) setCombine(data.combine);
          // Only restore dataPath if not in URL
          if (!dataPathFromUrl && data.dataPath) setDataPath(data.dataPath);
        }
      }
    } catch (e) {
      console.warn('Failed to load session state:', e);
    }
  }, []); // Only on mount
  
  // Persist session state on changes
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        templateId: selectedTemplateId,
        columnMap,
        fontMap,
        placeholderSettings,
        idColumn,
        combine,
        dataPath,
      }));
    } catch (e) {
      console.warn('Failed to persist session state:', e);
    }
  }, [selectedTemplateId, columnMap, fontMap, placeholderSettings, idColumn, combine, dataPath]);

  // Fetch templates list
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const result = await getTemplates(currentBrandId);
        if (result.success) {
          setTemplates(result.templates || []);
        }
      } catch (err) {
        console.error("Failed to fetch templates:", err);
      }
    };
    fetchTemplates();
  }, [currentBrandId]);

  // Fetch available fonts
  useEffect(() => {
    const fetchFonts = async () => {
      try {
        const result = await getFonts(currentBrandId);
        if (result.success) {
          setAvailableFonts(result.fonts || []);
        }
      } catch (err) {
        console.error("Failed to fetch fonts:", err);
      }
    };
    fetchFonts();
  }, [currentBrandId]);

  // Fetch previously uploaded data files
  useEffect(() => {
    if (!dataPath) lastFetchedDataPathRef.current = null;
    const fetchDataFiles = async () => {
      try {
        const result = await getDataFiles(currentBrandId);
        if (result.success) {
          const files = result.dataFiles || [];
          setDataFiles(files);
          
          // Restore dataInfo if we have dataPath from URL but no dataInfo — fetch once per dataPath
          if (dataPath && !dataInfo) {
            const dataFileId = parseInt(dataPath, 10);
            if (Number.isNaN(dataFileId)) return;
            if (lastFetchedDataPathRef.current === dataFileId) return;
            lastFetchedDataPathRef.current = dataFileId;
            const dataFile = files.find((d) => d.id === dataFileId);
            if (dataFile) {
              try {
                const [dataResult, previewResult] = await Promise.all([
                  getDataFile(dataFileId),
                  getDataFilePreview(dataFileId, 10),
                ]);
                
                if (dataResult.success) {
                  const data = dataResult.dataFile || dataResult;
                  setDataInfo({
                    id: data.id,
                    columns: data.columns || [],
                    rowCount: data.row_count,
                    preview: previewResult.preview || previewResult.preview_data || [],
                  });
                }
              } catch (fetchErr) {
                console.warn("Failed to fetch full data file details:", fetchErr);
                // Fallback to basic info
                setDataInfo({
                  id: dataFile.id,
                  columns: dataFile.columns || [],
                  rowCount: dataFile.row_count,
                  preview: dataFile.preview_data || [],
                });
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch data files:", err);
      }
    };
    fetchDataFiles();
  }, [dataPath, currentBrandId]);

  // Clear preview error when template changes
  useEffect(() => {
    setPreviewSectionError(false);
  }, [selectedTemplateId]);

  // Fetch template details when selected + auto-match fonts
  useEffect(() => {
    if (!selectedTemplateId) return;

    const fetchTemplate = async () => {
      setLoading(true);
      try {
        const result = await getTemplate(selectedTemplateId);
        if (result.success) {
          setTemplate(result.template);
          setColumnMap(result.template.column_map || {});
          setPlaceholderSettings(result.template.placeholder_settings || result.template.ignore_width || {});

          // Auto-match fonts if available
          const autoFontMap = { ...result.template.font_map };
          const placeholders = result.template.placeholders || [];

          for (const ph of placeholders) {
            if (!autoFontMap[ph.name] && ph.font) {
              // Try to find matching font file
              const matchedFont = findMatchingFont(ph.font, availableFonts);
              if (matchedFont) {
                autoFontMap[ph.name] = matchedFont.filename;
              }
            }
          }

          setFontMap(autoFontMap);
        }
      } catch (err) {
        message.error("Failed to fetch template");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [selectedTemplateId, availableFonts]);

  // Helper function to find matching font
  const findMatchingFont = (pdfFontName, fonts) => {
    if (!pdfFontName || !fonts.length) return null;

    const normalizedPdfFont = pdfFontName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

    for (const font of fonts) {
      const normalizedAvailableFont = font.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      const normalizedFilename = (font.filename || font.name)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

      // Check for exact or close match
      if (
        normalizedAvailableFont.includes(normalizedPdfFont) ||
        normalizedPdfFont.includes(normalizedAvailableFont) ||
        normalizedFilename.includes(normalizedPdfFont)
      ) {
        return font;
      }
    }
    return null;
  };

  // Extract placeholders from custom regions
  const customRegionPlaceholders = useMemo(() => {
    const customRegions = placeholderSettings._customRegions || [];
    const phNames = new Set();
    
    customRegions.forEach(region => {
      const content = region.content || "";
      const matches = content.match(/\{([A-Z0-9_ ]+)\}/gi) || [];
      matches.forEach(match => {
        const name = match.replace(/[{}]/g, "").toUpperCase();
        phNames.add(name);
      });
    });
    
    return Array.from(phNames).map(name => ({
      name,
      font: "Custom Region",
      size: 11,
      isFromCustomRegion: true,
    }));
  }, [placeholderSettings._customRegions]);

  // Combined placeholders (template + custom regions)
  const allPlaceholders = useMemo(() => {
    const templatePhs = template?.placeholders || [];
    const existingNames = new Set(templatePhs.map(p => p.name.toUpperCase()));
    
    // Add custom region placeholders that don't exist in template
    const newPhs = customRegionPlaceholders.filter(
      p => !existingNames.has(p.name.toUpperCase())
    );
    
    return [...templatePhs, ...newPhs];
  }, [template?.placeholders, customRegionPlaceholders]);

  // Calculate font status for each placeholder
  const fontStatus = useMemo(() => {
    if (!allPlaceholders.length)
      return { matched: [], missing: [], usingDefault: [] };

    const matched = [];
    const missing = [];
    const usingDefault = [];

    for (const ph of allPlaceholders) {
      // Skip custom region placeholders - they use Helvetica by default
      if (ph.isFromCustomRegion) {
        matched.push({ ...ph, mappedFont: fontMap[ph.name] || "Helvetica", autoMatched: true });
        continue;
      }
      
      if (fontMap[ph.name]) {
        matched.push({ ...ph, mappedFont: fontMap[ph.name] });
      } else if (findMatchingFont(ph.font, availableFonts)) {
        const matchedFont = findMatchingFont(ph.font, availableFonts);
        matched.push({
          ...ph,
          mappedFont: matchedFont.filename || matchedFont.name,
          autoMatched: true,
        });
      } else {
        // Check if font exists in available fonts
        const fontExists = availableFonts.some(
          (f) =>
            f.name.toLowerCase().includes(ph.font?.toLowerCase()) ||
            ph.font?.toLowerCase().includes(f.name.toLowerCase())
        );
        if (fontExists) {
          usingDefault.push(ph);
        } else {
          missing.push(ph);
        }
      }
    }

    return { matched, missing, usingDefault };
  }, [template, fontMap, availableFonts, allPlaceholders]);

  const handleDataUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const result = await uploadDataFile(file, file.name);
      if (result.success) {
        const data = result.dataFile || result;
        const newDataPath = data.id.toString();
        handleDataPathChange(newDataPath); // Use ID as reference (stored in DB)
        
        // Fetch preview data
        const previewResult = await getDataFilePreview(data.id, 10);
        
        setDataInfo({
          id: data.id,
          columns: data.columns || [],
          rowCount: data.row_count,
          preview: previewResult.preview || [],
        });
        message.success(
          `Loaded ${data.row_count || 0} rows with ${
            data.columns?.length || 0
          } columns`
        );
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDataFile = async (dataFileId) => {
    handleDataPathChange(dataFileId.toString()); // Use ID as reference (stored in DB)
    setLoading(true);
    
    try {
      // Fetch full data file info with preview
      const [dataResult, previewResult] = await Promise.all([
        getDataFile(dataFileId),
        getDataFilePreview(dataFileId, 10),
      ]);
      
      if (dataResult.success) {
        const data = dataResult.dataFile || dataResult;
        setDataInfo({
          id: data.id,
          columns: data.columns || [],
          rowCount: data.row_count,
          preview: previewResult.preview || previewResult.preview_data || [],
        });
        message.success(
          `Loaded ${data.row_count || 0} rows with ${data.columns?.length || 0} columns`
        );
      }
    } catch (err) {
      console.error("Failed to load data file:", err);
      message.error("Failed to load data file details");
      
      // Fallback to basic info from list
      const dataFile = dataFiles.find((d) => d.id === dataFileId);
      if (dataFile) {
        setDataInfo({
          id: dataFile.id,
          columns: dataFile.columns || [],
          rowCount: dataFile.row_count,
          preview: dataFile.preview_data || [],
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleColumnMapChange = (placeholder, column) => {
    // Store as placeholder -> column mapping for clarity
    setColumnMap((prev) => {
      const updated = { ...prev };
      // Remove any existing mapping for this placeholder
      Object.keys(updated).forEach((key) => {
        if (updated[key] === placeholder || key === placeholder) {
          delete updated[key];
        }
      });
      // Add new mapping: column -> placeholder (what backend expects)
      if (column && column !== placeholder) {
        updated[column] = placeholder;
      }
      return updated;
    });
  };

  const COLUMN_ALIASES = {
    title: ["clienttitle", "titre", "anrede", "civilité", "civilite"],
    firstname: ["firstname", "prenom", "prénom", "vorname", "firstnam"],
    lastname: ["lastname", "surname", "nom", "nachname", "familienname"],
    zipcode: ["zipcode", "postalcode", "codepostal", "plz", "zip"],
    address1: ["address1", "adresse1", "strasse", "ad1"],
    address2: ["address2", "adresse2", "ad2"],
    address3: ["address3", "adresse3", "ad3"],
    city: ["city", "ville", "stadt", "ort"],
    country: ["country", "pays", "land"],
    state: ["state", "état", "etat", "bundesland"],
    phone: ["phone", "telephone", "téléphone", "telefon"],
    gender: ["gender", "sexe", "geschlecht"],
    dataentrycode: ["dataentrycode", "codeaction"],
    campaigncode: ["campaigncode"],
    offer: ["offer", "offre"],
    clientid: ["clientid", "numéroclient", "numeroclient"],
  };

  const getColumnForPlaceholder = (placeholderName) => {
    for (const [col, ph] of Object.entries(columnMap)) {
      if (ph === placeholderName || ph.toUpperCase() === placeholderName.toUpperCase()) return col;
    }
    if (!dataInfo?.columns) return null;
    const phNorm = placeholderName.toLowerCase().replace(/[_{}\s-]/g, "");
    for (const col of dataInfo.columns) {
      const colNorm = col.toLowerCase().replace(/[_{}\s-]/g, "");
      if (colNorm === phNorm) return col;
      const aliasGroup = Object.entries(COLUMN_ALIASES).find(([, vals]) => vals.includes(phNorm) || phNorm === vals[0]);
      if (aliasGroup) {
        const [key, vals] = aliasGroup;
        if (colNorm === key || vals.includes(colNorm)) return col;
      }
    }
    return null;
  };

  const handleFontMapChange = (placeholder, fontFilename) => {
    setFontMap((prev) => ({ ...prev, [placeholder]: fontFilename }));
  };

  const handleTestGenerate = async () => {
    if (!selectedTemplateId || !dataPath) {
      message.warning("Please select a template and upload data");
      return;
    }

    // Check if custom regions exist
    const customRegions = placeholderSettings._customRegions || [];
    if (customRegions.length > 0) {
      message.info(`Generating with ${customRegions.length} custom region(s)...`);
    }

    setGenerating(true);
    try {
      console.log("Generating test PDF with:", {
        templateId: selectedTemplateId,
        dataPath,
        customRegions: customRegions.length,
        placeholderSettings,
      });
      
      // Log exact coordinates being sent
      if (customRegions.length > 0) {
        console.log("Custom regions coordinates being sent:", customRegions.map(r => ({
          id: r.id,
          page: r.page,
          x: r.x,
          y: r.y,
          width: r.width,
          height: r.height,
          content: r.content?.substring(0, 30),
        })));
      }

      const blob = await testGenerate({
        templateId: selectedTemplateId,
        dataPath,
        rowIndex: 0,
        columnMap,
        fontMap,
        placeholderSettings,
      });

      // Check if response is actually a PDF blob
      if (blob && blob.size > 0) {
        // Verify it's a PDF (starts with %PDF)
        const firstBytes = await blob.slice(0, 5).text();
        if (firstBytes.startsWith('%PDF')) {
          const blobUrl = URL.createObjectURL(blob);
          setPreviewUrl(blobUrl);
          setPreviewVisible(true);
          message.success("Test PDF generated!");
          setGenerationResult({ batchId: "test", previewUrl: blobUrl });
        } else {
          // Response might be JSON error
          const text = await blob.text();
          try {
            const errorData = JSON.parse(text);
            message.error(errorData.error || "Generation failed");
          } catch {
            message.error("Invalid PDF response");
          }
        }
      } else {
        message.error("Empty response from server");
      }
    } catch (err) {
      console.error("Generate error:", err);
      // Handle blob error responses
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          message.error(errorData.error || "Generation failed");
        } catch {
          message.error(err.message || "Generation failed");
        }
      } else {
        message.error(err.response?.data?.error || err.message || "Generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (!selectedTemplateId || !dataPath) {
      message.warning("Please select a template and upload data");
      return;
    }

    setGenerating(true);
    try {
      const result = await generateBatch({
        templateId: selectedTemplateId,
        dataPath,
        columnMap,
        fontMap,
        placeholderSettings,
        idColumn,
        combine,
        brandId: currentBrandId,
      });
      
      if (result.success) {
        message.success("Batch generation started!");
        setGenerationResult(result);
        navigate(`/pdf-parser/history?generationId=${result.generationId}`);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message || "Batch generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveMappings = async () => {
    if (!selectedTemplateId) {
      message.warning("Please select a template first");
      return;
    }

    try {
      console.log("Saving mappings:", { columnMap, fontMap });
      const result = await updateTemplateMappings(
        selectedTemplateId,
        columnMap,
        fontMap,
        placeholderSettings
      );
      if (result.success) {
        message.success("Mappings saved successfully!");
      } else {
        message.error(result.error || "Failed to save mappings");
      }
    } catch (err) {
      console.error("Save mappings error:", err);
      message.error(
        "Failed to save mappings: " + (err.message || "Unknown error")
      );
    }
  };

  // Fetch all generated files for batch preview
  const fetchBatchFiles = async (generationId) => {
    if (!generationId) return;
    
    setLoadingBatchFiles(true);
    try {
      const result = await getGenerationFiles(generationId);
      if (result.success) {
        setBatchFiles(result.files || []);
        setBatchPreviewVisible(true);
      } else {
        message.error("Failed to load batch files");
      }
    } catch (err) {
      message.error("Failed to load batch files: " + err.message);
    } finally {
      setLoadingBatchFiles(false);
    }
  };

  const handleDownloadZip = async (generationId) => {
    if (!generationId) return;
    try {
      message.loading({ content: "Preparing ZIP...", key: "zip-download" });
      const blob = await downloadGenerationZip(generationId);
      if (!blob || blob.size === 0) {
        throw new Error("Empty response from server");
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `generation_${generationId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success({ content: "ZIP downloaded!", key: "zip-download" });
    } catch (err) {
      console.error("ZIP download failed:", err);
      message.error({ content: "Failed to download ZIP: " + err.message, key: "zip-download" });
    }
  };

  // If no brand selected, show message
  if (!hasSelectedBrands) {
    return (
      <Card style={{ textAlign: "center", padding: 60 }}>
        <Empty 
          description="Please select a brand to use PDF generation"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* Template & Data Selection */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: "#1a1a2e",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileTextOutlined style={{ color: "#fff", fontSize: 14 }} />
                </div>
                <span style={{ fontWeight: 600 }}>
                  1. Select Template & Data
                </span>
              </div>
            }
            style={{ borderRadius: 12 }}
          >
            <Form layout="vertical">
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Template</span>}
              >
                <Select
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  placeholder="Select a template"
                  style={{ width: "100%" }}
                  loading={loading}
                >
                  {templates.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.name} ({t.placeholders?.length || 0} placeholders)
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Font Status Alert */}
              {template && (
                <div style={{ marginBottom: 16 }}>
                  {fontStatus.missing.length > 0 ? (
                    <Alert
                      message={
                        <span>
                          <WarningOutlined style={{ marginRight: 8 }} />
                          {fontStatus.missing.length} font(s) not found - will
                          use Helvetica
                        </span>
                      }
                      description={
                        <div style={{ fontSize: 12 }}>
                          Missing:{" "}
                          {fontStatus.missing.map((p) => p.font).join(", ")}
                          <br />
                          <Button
                            size="small"
                            type="link"
                            onClick={() => navigate("/pdf-parser/fonts")}
                            style={{ padding: 0, marginTop: 4 }}
                          >
                            Upload fonts →
                          </Button>
                        </div>
                      }
                      type="warning"
                      showIcon={false}
                      style={{ borderRadius: 8 }}
                    />
                  ) : fontStatus.matched.length > 0 ? (
                    <Alert
                      message={
                        <span>
                          <CheckCircleOutlined
                            style={{ marginRight: 8, color: "#52c41a" }}
                          />
                          All fonts matched!
                        </span>
                      }
                      type="success"
                      showIcon={false}
                      style={{ borderRadius: 8 }}
                    />
                  ) : null}
                </div>
              )}

              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Data File</span>}
              >
                {dataInfo ? (
                  <Alert
                    message={
                      <span>
                        <CheckCircleOutlined
                          style={{ marginRight: 8, color: "#52c41a" }}
                        />
                        Data loaded: {dataInfo.rowCount || "?"} rows,{" "}
                        {dataInfo.columns?.length || 0} columns
                      </span>
                    }
                    type="success"
                    showIcon={false}
                    action={
                      <Button
                        size="small"
                        onClick={() => {
                          setDataInfo(null);
                          handleDataPathChange("");
                        }}
                      >
                        Change
                      </Button>
                    }
                    style={{ borderRadius: 8 }}
                  />
                ) : (
                  <div>
                    {dataFiles.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <Select
                          placeholder="Select existing data file"
                          style={{ width: "100%" }}
                          onChange={handleSelectDataFile}
                          allowClear
                        >
                          {dataFiles.map((df) => (
                            <Option
                              key={df.id}
                              value={df.id}
                              disabled={!df.has_file}
                            >
                              {!df.has_file && (
                                <WarningOutlined
                                  style={{ color: "#faad14", marginRight: 4 }}
                                />
                              )}
                              {df.name} ({df.row_count} rows)
                              {!df.has_file && (
                                <span
                                  style={{
                                    color: "#999",
                                    fontSize: 11,
                                    marginLeft: 4,
                                  }}
                                >
                                  (needs re-upload)
                                </span>
                              )}
                            </Option>
                          ))}
                        </Select>
                        <Divider style={{ margin: "8px 0", fontSize: 12 }}>
                          or upload new
                        </Divider>
                      </div>
                    )}
                    <input
                      type="file"
                      className="form-control"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleDataUpload}
                      style={{ borderRadius: 8 }}
                    />
                  </div>
                )}
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    ID Column (for filenames)
                  </span>
                }
              >
                <Select
                  value={idColumn}
                  onChange={setIdColumn}
                  style={{ width: "100%" }}
                >
                  {dataInfo?.columns?.map((col) => (
                    <Option key={col} value={col}>
                      {col}
                    </Option>
                  ))}
                  <Option value="person_id">person_id (default)</Option>
                </Select>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Column & Font Mapping */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: "#16213e",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LinkOutlined style={{ color: "#fff", fontSize: 14 }} />
                </div>
                <span style={{ fontWeight: 600 }}>
                  2. Column & Font Mapping
                </span>
              </div>
            }
            extra={
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={saveMappings}
                disabled={!selectedTemplateId}
                style={{ 
                  background: "#1677ff",
                  borderColor: "#1677ff",
                  color: "#fff"
                }}
              >
                Save Mappings
              </Button>
            }
            style={{ borderRadius: 12 }}
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin />
              </div>
            ) : allPlaceholders.length > 0 ? (
              <Table
                dataSource={allPlaceholders}
                columns={[
                  {
                    title: "Placeholder",
                    dataIndex: "name",
                    key: "name",
                    width: 140,
                    render: (name, record) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Tag style={{ 
                          background: record.isFromCustomRegion ? "#f6ffed" : "#e6f7ff",
                          border: record.isFromCustomRegion ? "1px solid #b7eb8f" : "1px solid #91d5ff",
                          color: record.isFromCustomRegion ? "#52c41a" : "#1890ff",
                        }}>
                          {`{${name}}`}
                        </Tag>
                        {record.isFromCustomRegion && (
                          <span style={{ fontSize: 9, color: "#888" }}>region</span>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: "Map to Column",
                    key: "column",
                    width: 180,
                    render: (_, record) => {
                      const mappedColumn = getColumnForPlaceholder(record.name);
                      const isAutoMatched =
                        mappedColumn &&
                        !Object.values(columnMap).includes(record.name);

                      return (
                        <div>
                          <Select
                            value={mappedColumn || undefined}
                            onChange={(val) =>
                              handleColumnMapChange(record.name, val)
                            }
                            style={{ width: "100%" }}
                            size="small"
                            placeholder="Select column..."
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            status={
                              !mappedColumn && dataInfo?.columns?.length > 0
                                ? "warning"
                                : undefined
                            }
                          >
                            {dataInfo?.columns?.map((col) => (
                              <Option key={col} value={col}>
                                {col}
                                {col.toLowerCase() ===
                                  record.name.toLowerCase() && (
                                  <span
                                    style={{
                                      color: "#52c41a",
                                      marginLeft: 8,
                                      fontSize: 10,
                                    }}
                                  >
                                    ● match
                                  </span>
                                )}
                              </Option>
                            ))}
                          </Select>
                          {isAutoMatched && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "#52c41a",
                                marginTop: 2,
                              }}
                            >
                              Auto-matched
                            </div>
                          )}
                          {!mappedColumn && dataInfo?.columns?.length > 0 && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "#faad14",
                                marginTop: 2,
                              }}
                            >
                              No column selected
                            </div>
                          )}
                          {!dataInfo?.columns?.length && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "#888",
                                marginTop: 2,
                              }}
                            >
                              Upload data first
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: (
                      <span>
                        <FontSizeOutlined style={{ marginRight: 4 }} />
                        Font
                      </span>
                    ),
                    key: "font",
                    width: 180,
                    render: (_, record) => {
                      // Custom region placeholders have their font set in the region editor
                      if (record.isFromCustomRegion) {
                        return (
                          <div style={{ fontSize: 11, color: "#888" }}>
                            Custom Region
                            <div style={{ fontSize: 10, color: "#52c41a", marginTop: 2 }}>
                              Set in Region Editor
                            </div>
                          </div>
                        );
                      }

                      const isAutoMatched = fontStatus.matched.some(
                        (m) => m.name === record.name && m.autoMatched
                      );
                      const isMissing = fontStatus.missing.some(
                        (m) => m.name === record.name
                      );

                      // Get currently selected font name for display
                      const selectedFontFilename = fontMap[record.name];
                      const selectedFont = selectedFontFilename
                        ? availableFonts.find((f) => f.filename === selectedFontFilename || f.name === selectedFontFilename)
                        : null;

                      return (
                        <div>
                          <Tooltip
                            title={selectedFont ? selectedFont.name : (isMissing ? "Helvetica (fallback)" : "Default font")}
                            placement="topLeft"
                          >
                            <Select
                              value={fontMap[record.name] || "default"}
                              onChange={(val) =>
                                handleFontMapChange(
                                  record.name,
                                  val === "default" ? null : val
                                )
                              }
                              style={{ width: "100%" }}
                              size="small"
                              status={isMissing ? "warning" : undefined}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children?.toString() || "")
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              dropdownStyle={{ minWidth: 250 }}
                            >
                              <Option value="default">
                                <span
                                  style={{
                                    color: isMissing ? "#faad14" : undefined,
                                  }}
                                >
                                  {isMissing ? "Helvetica" : `Default`}
                                </span>
                              </Option>
                              {availableFonts.map((f) => (
                                <Option key={f.id || f.filename} value={f.filename || f.name} title={f.name}>
                                  <Tooltip title={f.name} placement="right" mouseEnterDelay={0.5}>
                                    <span>
                                      {f.name}
                                      {findMatchingFont(record.font, [f]) && (
                                        <Badge
                                          status="success"
                                          style={{ marginLeft: 8 }}
                                        />
                                      )}
                                    </span>
                                  </Tooltip>
                                </Option>
                              ))}
                            </Select>
                          </Tooltip>
                          <div
                            style={{
                              fontSize: 10,
                              color: "#888",
                              marginTop: 2,
                            }}
                          >
                            {record.font} • {record.size}pt
                            {isAutoMatched && (
                              <Tag
                                style={{ marginLeft: 4, fontSize: 9, background: "#f6ffed", border: "1px solid #b7eb8f", color: "#52c41a" }}
                              >
                                Auto
                              </Tag>
                            )}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: "Style",
                    key: "style",
                    width: 180,
                    render: (_, record) => {
                      // Custom region placeholders have their style set in the region editor
                      if (record.isFromCustomRegion) {
                        return (
                          <span style={{ fontSize: 11, color: "#888" }}>
                            Set in Region Editor
                          </span>
                        );
                      }

                      const settings = placeholderSettings[record.name] || {};
                      const updateSetting = (key, value) => {
                        setPlaceholderSettings((prev) => ({
                          ...prev,
                          [record.name]: { ...prev[record.name], [key]: value },
                        }));
                      };

                      return (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                          <Select
                            value={settings.fontSize || 11}
                            onChange={(val) => updateSetting("fontSize", val)}
                            size="small"
                            style={{ width: 60 }}
                            title="Font Size"
                          >
                            {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24].map((s) => (
                              <Option key={s} value={s}>{s}pt</Option>
                            ))}
                          </Select>
                          <input
                            type="color"
                            value={settings.color || "#000000"}
                            onChange={(e) => updateSetting("color", e.target.value)}
                            style={{ width: 24, height: 20, border: "1px solid #d9d9d9", borderRadius: 3, cursor: "pointer", padding: 0 }}
                            title="Text Color"
                          />
                        </div>
                      );
                    },
                  },
                  {
                    title: "Options",
                    key: "options",
                    width: 200,
                    render: (_, record) => {
                      const settings = placeholderSettings[record.name] || {};
                      const updateSetting = (key, value) => {
                        setPlaceholderSettings((prev) => ({
                          ...prev,
                          [record.name]: { ...prev[record.name], [key]: value },
                        }));
                      };

                      const isParagraph = settings.type === "paragraph";
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          <div style={{ display: "flex", gap: 4 }}>
                            <Tooltip title="Text alignment">
                              <Select
                                value={settings.alignment || "left"}
                                onChange={(val) =>
                                  updateSetting("alignment", val)
                                }
                                size="small"
                                style={{ width: 70 }}
                              >
                                <Option value="left">Left</Option>
                                <Option value="center">Center</Option>
                                <Option value="right">Right</Option>
                                {isParagraph && <Option value="justify">Justify</Option>}
                              </Select>
                            </Tooltip>
                            <Tooltip title="Overflow: Shrink/Keep/Cut">
                              <Select
                                value={settings.overflow || "keep"}
                                onChange={(val) =>
                                  updateSetting("overflow", val)
                                }
                                size="small"
                                style={{ width: 70 }}
                              >
                                <Option value="shrink">Shrink</Option>
                                <Option value="keep">Keep</Option>
                                <Option value="truncate">Cut</Option>
                              </Select>
                            </Tooltip>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title="Paragraph mode (multi-line)">
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  cursor: "pointer",
                                  fontSize: 11,
                                }}
                              >
                                <Switch
                                  size="small"
                                  checked={isParagraph}
                                  onChange={(checked) =>
                                    updateSetting(
                                      "type",
                                      checked ? "paragraph" : "single"
                                    )
                                  }
                                />
                                <span>Paragraph</span>
                              </label>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
                rowKey="name"
                size="small"
                pagination={false}
                scroll={{ y: 400 }}
              />
            ) : (
              <Alert
                message="Select a template to see placeholders"
                type="info"
                style={{ borderRadius: 8 }}
              />
            )}
          </Card>
        </Col>

        {/* Placeholder Visualization - Collapsible */}
        {selectedTemplateId && (
          <Col xs={24}>
            {previewSectionError && (
              <Alert
                message="Template preview couldn't load. You can still use Test (1 PDF) or Generate Batch below."
                type="info"
                showIcon
                closable
                onClose={() => setPreviewSectionError(false)}
                style={{ marginBottom: 16, borderRadius: 8 }}
              />
            )}
            <Card
              title={
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: "#2c3e50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BorderOutlined style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <span style={{ fontWeight: 600 }}>
                    Placeholder Visualization (Optional)
                  </span>
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    Add custom text regions
                  </Tag>
                </div>
              }
              style={{ borderRadius: 12 }}
              bodyStyle={{ padding: 16 }}
              extra={
                <Button size="small" type="link" onClick={() => window.location.reload()}>
                  Reload if not loading
                </Button>
              }
            >
              <PlaceholderVisualizer
                templateId={selectedTemplateId}
                onError={() => setPreviewSectionError(true)}
                placeholderSettings={placeholderSettings}
                fontMap={fontMap}
                availableFonts={availableFonts}
                dataFileId={dataInfo?.id || (dataPath ? parseInt(dataPath, 10) : null)}
                availablePlaceholders={[
                  // From template data
                  ...(template?.placeholders?.map(p => p.name) || []),
                  // From data columns (these can be used as placeholders)
                  ...(dataInfo?.columns?.map(c => c.replace(/[{}]/g, "").toUpperCase()) || []),
                  // Common placeholders as fallback
                  "FIRSTNAME", "LASTNAME", "TITLE", "ADDRESS1", "CITY", "STATE", "ZIPCODE",
                  "PARAGRAPH1", "PARAGRAPH2", "NAME", "EMAIL", "PHONE", "DATE", "COMPANY"
                ].filter((v, i, a) => a.indexOf(v) === i)} // Remove duplicates
                onPlaceholderSelect={(placeholder) => {
                  console.log("Selected placeholder:", placeholder);
                }}
                onRegionsChange={async (regions) => {
                  // Store custom regions with their content templates
                  // regions is an array of {id, x, y, width, height, page, content, fontSize, color, align, lineHeight}
                  console.log("Saving custom regions:", regions);
                  
                  const newSettings = {
                    ...placeholderSettings,
                    _customRegions: regions,
                  };
                  
                  setPlaceholderSettings(newSettings);
                  
                  // Auto-save to database
                  if (selectedTemplateId) {
                    try {
                      const result = await updateTemplateMappings(
                        selectedTemplateId,
                        columnMap,
                        fontMap,
                        newSettings
                      );
                      if (result.success) {
                        message.success(`Saved ${regions.length} region(s) to template.`);
                      } else {
                        message.error("Failed to save regions to database");
                      }
                    } catch (error) {
                      console.error("Error saving regions:", error);
                      message.error("Failed to save regions");
                    }
                  }
                }}
              />
            </Card>
          </Col>
        )}

        {/* Generation Options */}
        <Col xs={24}>
          <Card
            title={
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: "#0f3460",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PlayCircleOutlined style={{ color: "#fff", fontSize: 14 }} />
                </div>
                <span style={{ fontWeight: 600 }}>3. Generate</span>
              </div>
            }
            style={{ borderRadius: 12 }}
          >
            <Row gutter={16} align="middle">
              <Col>
                <Space>
                  <span style={{ fontWeight: 500 }}>Combine all PDFs:</span>
                  <Switch checked={combine} onChange={setCombine} />
                </Space>
              </Col>
              <Col flex="auto" />
              <Col>
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={handleTestGenerate}
                    loading={generating}
                    disabled={!selectedTemplateId || !dataPath}
                    style={{ 
                      borderRadius: 8,
                      background: "#1677ff",
                      borderColor: "#1677ff",
                      color: "#fff"
                    }}
                  >
                    Test (1 PDF)
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleBatchGenerate}
                    loading={generating}
                    disabled={!selectedTemplateId || !dataPath}
                    size="large"
                    style={{
                      borderRadius: 8,
                      background: "#1677ff",
                      borderColor: "#1677ff",
                      color: "#fff",
                    }}
                  >
                    Generate Batch
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Generation Result */}
            {generationResult && (
              <div className="mt-3">
                <Divider />
                <Alert
                  message="Generation Complete"
                  description={
                    <div>
                      <p>
                        Batch ID: <code>{generationResult.batchId}</code>
                      </p>
                      <Space>
                        {previewUrl ? (
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => setPreviewVisible(true)}
                          >
                            Preview PDF
                          </Button>
                        ) : (
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            loading
                            disabled
                          >
                            Loading Preview...
                          </Button>
                        )}
                        {generationResult?.generationId && (
                          <>
                            <Button
                              icon={<AppstoreOutlined />}
                              onClick={() => fetchBatchFiles(generationResult.generationId)}
                              loading={loadingBatchFiles}
                            >
                              Preview All
                            </Button>
                            <Button
                              icon={<FileTextOutlined />}
                              onClick={() => handleDownloadZip(generationResult.generationId)}
                            >
                              Download All (ZIP)
                            </Button>
                          </>
                        )}
                        {previewUrl && (
                          <Button
                            icon={<DownloadOutlined />}
                            href={previewUrl}
                            download="preview.pdf"
                          >
                            Download
                          </Button>
                        )}
                      </Space>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ borderRadius: 8 }}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* PDF Preview Modal */}
      <Modal
        title="PDF Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        zIndex={999999}
        styles={{ mask: { zIndex: 999998 } }}
        style={{ top: 20 }}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            href={previewUrl}
            target="_blank"
          >
            Download
          </Button>,
        ]}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            style={{ width: "100%", height: "70vh", border: "none" }}
            title="PDF Preview"
          />
        )}
      </Modal>

      {/* Batch Preview Modal */}
      <Modal
        title={`Batch Preview (${batchFiles.length} files)`}
        open={batchPreviewVisible}
        onCancel={() => setBatchPreviewVisible(false)}
        width="90%"
        zIndex={999999}
        styles={{ mask: { zIndex: 999998 } }}
        style={{ top: 20 }}
        footer={[
          <Button key="close" onClick={() => setBatchPreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="download-all"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadZip(generationResult?.generationId)}
          >
            Download All (ZIP)
          </Button>,
        ]}
      >
        {loadingBatchFiles ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading previews...</p>
          </div>
        ) : (
          <div 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: 16,
              maxHeight: '70vh',
              overflow: 'auto',
              padding: 8,
            }}
          >
            {batchFiles.map((file, index) => (
              <div
                key={file.id}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <div 
                  style={{ 
                    height: 250, 
                    background: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser/generated-files/${file.id}/thumbnail/`}
                    alt={file.filename}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="color:#999;padding:20px;text-align:center">Preview unavailable</div>';
                    }}
                  />
                </div>
                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ 
                    fontSize: 12, 
                    fontWeight: 500, 
                    marginBottom: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    #{index + 1} - {file.row_id || file.filename}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="small"
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser/generated-files/${file.id}/download/`, '_blank')}
                      style={{ padding: 0 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser/generated-files/${file.id}/download/`;
                        link.download = file.filename;
                        link.click();
                      }}
                      style={{ padding: 0 }}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default GeneratePdf;
